/**
 * M13: kereskedelmi útvonalak (létrehozás, lista, blokád jelzés) és megaprojektek.
 * Minden gomb szerverkérés; a lista mindig a szerver válaszát mutatja.
 */
import { useCallback, useEffect, useState } from 'react';
import type { DiplomacyOverview, MegaprojectView, TradeRouteView } from '@galaxis/shared';
import { TRADE_RULES } from '@galaxis/shared';
import { apiFetch } from '../api/client.ts';
import { fetchDiplomacyOverview } from '../api/galaxy.ts';
import { useGameStore } from '../store/game-store.ts';

const key = (p: string) => `${p}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const RES = { metal: 'fém', energy: 'energia' } as const;

function RoutesPanel() {
  const { galaxyMap, me, pushToast } = useGameStore();
  const [routes, setRoutes] = useState<TradeRouteView[]>([]);
  const [limit, setLimit] = useState(0);
  const [overview, setOverview] = useState<DiplomacyOverview | null>(null);
  const [partner, setPartner] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [exportRes, setExportRes] = useState<'metal' | 'energy'>('metal');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiFetch<{ routes: TradeRouteView[]; limit: number }>('/trade-routes').then(r => { setRoutes(r.routes); setLimit(r.limit); }).catch(() => undefined);
    fetchDiplomacyOverview().then(setOverview).catch(() => undefined);
  }, []);
  useEffect(() => { load(); }, [load]);

  const myCiv = me?.civilization?.id;
  const partners = (overview?.relations ?? []).filter(r => r.treaties.some(t => t.type === 'trade'));
  const systems = galaxyMap?.systems ?? [];
  const mine = systems.filter(s => s.owner?.civilizationId === myCiv || s.isHomeSystem);
  const theirs = systems.filter(s => partner && s.owner?.civilizationId === partner);
  const owned = routes.filter(r => r.role === 'owner').length;

  const create = async () => {
    setBusy(true);
    try {
      await apiFetch('/trade-routes', { method: 'POST', body: JSON.stringify({ partnerCivId: partner, fromSystemId: from, toSystemId: to, exportResource: exportRes, idempotencyKey: key('route') }) });
      pushToast('Kereskedelmi útvonal létrehozva');
      load();
    } catch (e) {
      pushToast(e instanceof Error ? e.message : 'Az útvonal nem jött létre', 'error');
    } finally { setBusy(false); }
  };
  const cancel = async (id: string) => {
    try { await apiFetch(`/trade-routes/${id}`, { method: 'DELETE' }); load(); }
    catch (e) { pushToast(e instanceof Error ? e.message : 'Hiba', 'error'); }
  };

  return (
    <section className="sci-fi-panel p-3 space-y-3" aria-label="Kereskedelmi útvonalak">
      <h2 className="font-mono text-sm tracking-widest text-accent">KERESKEDELMI ÚTVONALAK <span className="text-slate-500">{owned}/{limit}</span></h2>
      {limit === 0 && <p className="text-xs text-slate-400">Útvonalakhoz kutasd ki a „Kereskedelmi hálózatok” technológiát, és köss kereskedelmi megállapodást egy civilizációval.</p>}
      {routes.length === 0 && limit > 0 && <p className="text-xs text-slate-400">Még nincs útvonalad.</p>}
      <ul className="space-y-2">
        {routes.map(r => (
          <li key={r.id} className="bg-slate-900/60 rounded px-3 py-2 text-xs space-y-1" data-testid="trade-route">
            <div className="flex justify-between gap-2">
              <span className="text-slate-200">{r.fromSystemName} → {r.toSystemName} <span className="text-slate-500">({r.hops} ugrás, {r.role === 'owner' ? 'saját' : 'partner'})</span></span>
              <span className={r.status === 'blockaded' ? 'text-red-400 font-bold' : 'text-emerald-400'}>{r.status === 'blockaded' ? 'BLOKÁD' : 'aktív'}</span>
            </div>
            <p className="text-slate-400">Partner: {r.partnerCivName} · szállított: {RES[r.exportResource]} · bevétel: {r.incomePerHour.metal > 0 ? `${r.incomePerHour.metal} fém/ó` : `${r.incomePerHour.energy} energia/ó`}
              {r.role === 'owner' && ` · fenntartás ${TRADE_RULES.upkeepMetalPerHour} fém/ó`}</p>
            {r.status === 'blockaded' && <p className="text-red-300">Blokád: {r.blockadedByCivName ?? 'ismeretlen'} hadiflottája zárja az útvonalat; a bevétel szünetel. Védd meg flottával, vagy kössön békét.</p>}
            <button className="text-slate-400 underline min-h-[44px]" onClick={() => void cancel(r.id)}>Megszüntetés</button>
          </li>
        ))}
      </ul>
      {limit > 0 && owned < limit && (
        <div className="space-y-2 text-xs">
          <label className="block text-slate-400">Partner (kereskedelmi megállapodással)
            <select aria-label="Partner" className="mt-1 w-full rounded bg-slate-800 border border-slate-600 text-slate-200 px-2" style={{ minHeight: 44 }} value={partner} onChange={e => { setPartner(e.target.value); setTo(''); }}>
              <option value="">— válassz —</option>
              {partners.map(p => <option key={p.civId} value={p.civId}>{p.civName}</option>)}
            </select>
          </label>
          <label className="block text-slate-400">Kiinduló rendszer (saját)
            <select aria-label="Kiinduló rendszer" className="mt-1 w-full rounded bg-slate-800 border border-slate-600 text-slate-200 px-2" style={{ minHeight: 44 }} value={from} onChange={e => setFrom(e.target.value)}>
              <option value="">— válassz —</option>
              {mine.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="block text-slate-400">Cél rendszer (a partneré)
            <select aria-label="Cél rendszer" className="mt-1 w-full rounded bg-slate-800 border border-slate-600 text-slate-200 px-2" style={{ minHeight: 44 }} value={to} onChange={e => setTo(e.target.value)}>
              <option value="">— válassz —</option>
              {theirs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="block text-slate-400">Exportált nyersanyag (a partner ezt kapja, te a másikat)
            <select aria-label="Exportált nyersanyag" className="mt-1 w-full rounded bg-slate-800 border border-slate-600 text-slate-200 px-2" style={{ minHeight: 44 }} value={exportRes} onChange={e => setExportRes(e.target.value as 'metal' | 'energy')}>
              <option value="metal">Fém</option>
              <option value="energy">Energia</option>
            </select>
          </label>
          <button disabled={busy || !partner || !from || !to} onClick={() => void create()} className="w-full py-2 px-3 rounded-lg font-medium disabled:opacity-50" style={{ background: 'rgba(52,211,153,0.12)', color: '#6ee7b7', border: '1px solid rgba(52,211,153,0.3)', minHeight: 44 }}>
            Útvonal létrehozása
          </button>
        </div>
      )}
    </section>
  );
}

function MegaprojectsPanel() {
  const { pushToast } = useGameStore();
  const [projects, setProjects] = useState<MegaprojectView[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(() => {
    apiFetch<{ projects: MegaprojectView[] }>('/megaprojects').then(r => setProjects(r.projects)).catch(() => undefined);
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = async (kind: string) => {
    setBusy(kind);
    try {
      await apiFetch('/megaprojects', { method: 'POST', body: JSON.stringify({ kind, idempotencyKey: key('mega') }) });
      pushToast('Megaprojekt elindítva');
      load();
    } catch (e) {
      pushToast(e instanceof Error ? e.message : 'A megaprojekt nem indult el', 'error');
    } finally { setBusy(null); }
  };

  return (
    <section className="sci-fi-panel p-3 space-y-3" aria-label="Megaprojektek">
      <h2 className="font-mono text-sm tracking-widest text-accent">MEGAPROJEKTEK</h2>
      <p className="text-xs text-slate-400">Hosszú, drága építkezések tartós hatással. Az ár a birodalom méretével nő; a kész projekt fém karbantartást igényel. Feloldja: „Megastruktúrák” technológia.</p>
      <ul className="space-y-2">
        {projects.map(p => (
          <li key={p.kind} className="bg-slate-900/60 rounded px-3 py-2 text-xs space-y-1" data-testid="megaproject">
            <div className="flex justify-between"><span className="text-slate-200">{p.name}</span><span className="text-slate-500">szint {p.doneLevel}/3</span></div>
            <p className="text-slate-400">{p.description}</p>
            {p.building && <p className="text-amber-300">Épül: {p.building.level}. szint, kész: {new Date(p.building.completesAt).toLocaleString()}</p>}
            {p.next && (
              <button disabled={busy !== null} onClick={() => void start(p.kind)} className="w-full py-2 px-3 rounded-lg text-left disabled:opacity-50" style={{ background: 'rgba(167,139,250,0.1)', color: '#c4b5fd', border: '1px solid rgba(167,139,250,0.3)', minHeight: 44 }}>
                {p.next.level}. szint — {p.next.metal}⚙️ {p.next.energy}⚡ · {Math.round(p.next.timeMs / 3_600_000 * 10) / 10} óra
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function TradeView() {
  return (
    <div className="flex-1 overflow-y-auto p-3 sm:p-6">
      <div className="mx-auto max-w-3xl grid gap-4 md:grid-cols-2">
        <RoutesPanel />
        <MegaprojectsPanel />
      </div>
    </div>
  );
}
